<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { DomainInputRequestInput } from '@/types/api/User/DomainInputRequest';
import AdminDomainForm from './AdminDomainForm.vue';
import { getAllSuppliers, getDomainById, SupplierForAdmin, updateDomain } from '@/services/api/userService';
import { MinimalPackageDto } from '@/types/api/Product/MinimalPackageDto';
import { getAllPackages } from '@/services/api/productService';

const route = useRoute();
const router = useRouter();
const id = computed(() => String(route.params.id));

const domain = ref<DomainInputRequestInput | null>(null);
const domainId = ref<number>(0);
const loading = ref(false);
const loadingMeta = ref(true);
const loadingDomain = ref(true);
const suppliers = ref<Array<SupplierForAdmin>>([]);
const packages = ref<Array<MinimalPackageDto>>([]);

async function loadMeta() {
  loadingMeta.value = true;
  try {
    const [sup, pack] = await Promise.all([getAllSuppliers(), getAllPackages()]);
    suppliers.value = sup ?? [];
    packages.value = pack ?? [];
  } finally {
    loadingMeta.value = false;
  }
}

async function loadDomain() {
  try {
    loadingDomain.value = true;
    const fetchedDomain = await getDomainById(Number.parseInt(id.value));
    domainId.value = fetchedDomain.domainId;
    if (!fetchedDomain) {
      throw new Error('Domain not found');
    }
    const toArray = <T>(s: Set<T> | T[] | undefined | null): T[] => {
      if (!s) return [];
      return Array.isArray(s) ? s : Array.from(s);
    };
    const getSchemaType = (cs: any): 'COLOR' | 'TEXT' => {
      const t = (cs?.schemaType ?? cs?.type ?? '').toString().toUpperCase();
      return t === 'TEXT' ? 'TEXT' : 'COLOR';
    };
    const getHex = (cs: any): string => cs?.hexCode ?? cs?.hex ?? cs?.value ?? '';
    const getPackageId = (p: any): number => {
      const pid = p?.packageId ?? p?.id ?? p?.code;
      return typeof pid === 'string' ? Number.parseInt(pid) : pid;
    };
    const companyWebsite = fetchedDomain.companyWebsite;
    const mapped: DomainInputRequestInput = {
      price: typeof fetchedDomain.price === 'number' ? fetchedDomain.price : null,
      logo: fetchedDomain.logo ?? null,
      urlSuffix: fetchedDomain.urlSuffix ?? null,
      companyWebsite: companyWebsite,
      released: fetchedDomain.released,
      supplierId: fetchedDomain.supplier.userId,
      colorSchemas: toArray(fetchedDomain.colorSchema).map((cs: any) => ({
        domainId: fetchedDomain.domainId ?? fetchedDomain.domainId ?? undefined,
        schemaType: getSchemaType(cs),
        hexCode: getHex(cs)
      })),
      packagesIds: toArray(fetchedDomain.packages)
        .map(getPackageId)
        .filter((x: any) => Number.isFinite(x)) as number[]
    };
    domain.value = mapped;
  } catch (e: any) {
    ElMessageBox.alert(
      e?.response?.data?.message ?? e?.message ?? 'Domain not found',
      'Error',
      { type: 'error' }
    ).then(() => router.push({ name: 'AdminDomainsList' }));
  } finally {
    loadingDomain.value = false;
  }
}

onMounted(async () => {
  loadingMeta.value = true;
  try {
    await Promise.all([loadMeta(), loadDomain()]);
  } finally {
    loadingMeta.value = false;
  }
});

async function onSubmit(payload: DomainInputRequestInput) {
  if (!domain.value) return;
  loading.value = true;
  try {
    await updateDomain(domainId.value, domain.value);
    ElMessage.success('Domain updated');
    router.push('/admin/domains');
  } catch (e: any) {
    ElMessage.error(e?.response?.data?.message ?? 'Failed to update domain');
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <div class="page">
    <h2 class="title">{{ $t('components.adminDashboard.editDomain.page.title') }}</h2>
    <el-card>
      <template #header>
        <div class="card-header">Domain #{{ id }}</div>
      </template>
      <el-skeleton v-if="loadingMeta || loadingDomain" :rows="6" animated />
      <AdminDomainForm
        v-else-if="domain"
        v-model="domain"
        :suppliers="suppliers"
        :packages="packages"
        :loading="loading"
        @submit="onSubmit"
      />
      <el-empty v-else description="No data" />
    </el-card>
  </div>
</template>

<style scoped>
.page { max-width: 920px; overflow-y: auto; max-height: 100dvh; margin: 24px auto; padding-bottom: 100px; }
.title { margin: 0 0 12px; }
.card-header { font-weight: 600; }
</style>
